import React, { useState, useEffect, useCallback } from 'react';
import {
  XMarkIcon,
  BellAlertIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

/**
 * LinaProactiveToast — Displays proactive notifications from Lina
 * as a floating panel near the AI assistant widget.
 *
 * Features:
 *  - Animated slide-in for new notifications
 *  - Section-based layout (icon + label + value)
 *  - Suggested action buttons → feed into AI assistant
 *  - Dismiss / mark-read interactions
 *  - Collapsible body for compact view
 *  - Urgency-based border color
 *
 * Props:
 *  @param {Array}    notifications - Array of proactive event objects
 *  @param {Function} onDismiss     - (eventId) => void
 *  @param {Function} onMarkRead    - (eventId) => void
 *  @param {Function} onAction      - ({ label, action, args }) => void — sends action to AI chat
 *  @param {Function} onDismissAll  - () => void
 */
export default function LinaProactiveToast({
  notifications = [],
  onDismiss,
  onMarkRead,
  onAction,
  onDismissAll,
}) {
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [visibleIds, setVisibleIds] = useState(new Set());

  // Animate new notifications in
  useEffect(() => {
    const newIds = notifications.map((n) => n._id);
    const timer = setTimeout(() => {
      setVisibleIds(new Set(newIds));
    }, 50);
    return () => clearTimeout(timer);
  }, [notifications]);

  const toggleExpand = useCallback((id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleAction = useCallback(
    (action, eventId) => {
      onMarkRead?.(eventId);
      onAction?.(action);
    },
    [onMarkRead, onAction]
  );

  if (notifications.length === 0) return null;

  return (
    <div className="fixed bottom-20 right-4 z-50 flex flex-col gap-2 max-w-sm w-full pointer-events-none">
      {/* Dismiss all button if multiple */}
      {notifications.length > 1 && (
        <div className="pointer-events-auto flex justify-end">
          <button
            onClick={onDismissAll}
            className="text-xs text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-200 
                       px-2 py-1 rounded transition-colors"
          >
            Dismiss all ({notifications.length})
          </button>
        </div>
      )}

      {notifications.slice(0, 3).map((event) => {
        const isExpanded = expandedIds.has(event._id);
        const isVisible = visibleIds.has(event._id);

        return (
          <div
            key={event._id}
            className={`
              pointer-events-auto bg-white dark:bg-gray-800 rounded-xl shadow-lg 
              border-l-4 ${_urgencyBorderColor(event.urgency)}
              transform transition-all duration-300 ease-out
              ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
            `}
          >
            {/* Header */}
            <div className="flex items-start gap-2 p-3 pb-2">
              <div className="flex-shrink-0 mt-0.5">
                <SparklesIcon className="w-4 h-4 text-indigo-500 dark:text-indigo-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white leading-tight">
                  {event.title}
                </h4>
                <p className="text-xs text-gray-500 dark:text-gray-300 mt-0.5 line-clamp-2">
                  {event.message}
                </p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {event.sections?.length > 0 && (
                  <button
                    onClick={() => toggleExpand(event._id)}
                    className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    title={isExpanded ? 'Collapse' : 'Expand'}
                  >
                    {isExpanded ? (
                      <ChevronUpIcon className="w-3.5 h-3.5 text-gray-400" />
                    ) : (
                      <ChevronDownIcon className="w-3.5 h-3.5 text-gray-400" />
                    )}
                  </button>
                )}
                <button
                  onClick={() => onDismiss?.(event._id)}
                  className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  title="Dismiss"
                >
                  <XMarkIcon className="w-3.5 h-3.5 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Expandable Sections */}
            {isExpanded && event.sections?.length > 0 && (
              <div className="px-3 pb-2 space-y-1.5 border-t border-gray-100 dark:border-gray-700 pt-2">
                {event.sections.map((section, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center gap-2 text-xs rounded-md px-2 py-1.5
                      ${section.alert
                        ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                        : 'bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300'
                      }`}
                  >
                    <span className="flex-shrink-0 text-sm">{section.icon}</span>
                    <span className="font-medium flex-shrink-0">{section.label}:</span>
                    <span className="truncate">{section.value}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Suggested Actions */}
            {event.suggestedActions?.length > 0 && (
              <div className="px-3 pb-2.5 flex flex-wrap gap-1.5">
                {event.suggestedActions.slice(0, 3).map((action, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleAction(action, event._id)}
                    className="text-xs px-2.5 py-1 rounded-full 
                             bg-indigo-50 dark:bg-indigo-900/30 
                             text-indigo-600 dark:text-indigo-300
                             hover:bg-indigo-100 dark:hover:bg-indigo-900/50 
                             transition-colors font-medium"
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            )}

            {/* Timestamp & unread indicator */}
            <div className="px-3 pb-2 flex items-center justify-between">
              <span className="text-[10px] text-gray-400 dark:text-gray-400">
                {_formatTimestamp(event.createdAt)}
              </span>
              {(event.status === 'pending' || event.status === 'delivered') && (
                <span className="w-2 h-2 rounded-full bg-indigo-500 flex-shrink-0" title="Unread" />
              )}
            </div>
          </div>
        );
      })}

      {notifications.length > 3 && (
        <div className="pointer-events-auto text-center">
          <span className="text-xs text-gray-400 dark:text-gray-400">
            +{notifications.length - 3} more notification{notifications.length - 3 !== 1 ? 's' : ''}
          </span>
        </div>
      )}
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function _urgencyBorderColor(urgency) {
  switch (urgency) {
    case 'critical':
      return 'border-red-500';
    case 'high':
      return 'border-orange-500';
    case 'medium':
      return 'border-indigo-500';
    case 'low':
    default:
      return 'border-gray-300 dark:border-gray-600';
  }
}

function _formatTimestamp(ts) {
  if (!ts) return '';
  const date = new Date(ts);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
